import type { BoxProps } from '@mui/material';
import { Box, Container, Typography, useTheme } from '@mui/material';
import { stegaClean } from '@sanity/client/stega';
import type { FC } from 'react';
import { useMemo } from 'react';
import { IconsMap } from '~/constants/iconsMap';
import { isKeyof } from '~/types';
import type { Usps as UspsType } from '~/types/sanity-typegen';
import type { USPsProps } from '~/types/sanity/components';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';

const gridAreaMap = ['b', 'c', 'd', 'e', 'f', 'g'];

export const Usps: FC<Omit<USPsProps, '_key' | '_type'>> = ({ heading, tiles }) => {
  const theme = useTheme();
  const getCompoundTestId = getCompoundTestIdGenerator('usps');

  const fontSizeMap: Record<UspsType['tiles'][0]['fontSize'], BoxProps['fontSize']> = useMemo(
    () => ({
      xs: theme.spacing(2),
      sm: theme.spacing(2.125),
      md: theme.spacing(2.25),
      lg: theme.spacing(2.375),
      xl: theme.spacing(2.5),
    }),
    [theme],
  );

  return tiles.length ? (
    <Box component="section" py={{ xs: 1.5, sm: 3, md: 6 }}>
      <Container>
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gridTemplateRows="repeat(4, auto)"
          gridTemplateAreas={{
            xs: `
              'a a a a a a b b b b b .'
              '. . c c c c c d d d d d'
              'e e e e e e e e e f f f'
              '. . . . g g g g g g . .'
            `,
            md: `
              '. . . . . c c d d . . .'
              'a a a b b c c d d g g g'
              'a a a b b e e e e g g g'
              '. . f f f e e e e g g g'
              '. . f f f e e e e . . .'
            `,
          }}
          gap={{ xs: 1, sm: 2, md: 3 }}
        >
          {/* Heading */}
          {heading && (
            <Box gridArea="a">
              <Typography
                data-testid={getCompoundTestId('heading')}
                variant="h5"
                component="h2"
                fontSize={({ typography }) => ({ sm: typography.h3.fontSize })}
                lineHeight={({ typography }) => ({ sm: typography.h3.lineHeight })}
                overflow="hidden"
                whiteSpace="pre-wrap"
              >
                {heading}
              </Typography>
            </Box>
          )}

          {/* Tiles */}
          {tiles.map(({ _key, backgroundColor, icon: { name: iconName }, title, fontSize }, i) => {
            const cleanedIconName = stegaClean(iconName);
            const cleanedBackgroundColor = stegaClean(backgroundColor);
            const Icon = isKeyof(IconsMap, cleanedIconName) ? IconsMap[cleanedIconName] : null;
            const fontSizeValue = fontSizeMap[fontSize] || theme.typography.body1.fontSize;

            return (
              <Box
                key={_key}
                data-testid={getCompoundTestId('tile')}
                gridArea={gridAreaMap[i]}
                p={{ xs: 1.5, sm: 2, md: 3 }}
                borderRadius={{ xs: 3, sm: 4, md: 6 }}
                bgcolor={cleanedBackgroundColor}
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                {Icon && (
                  <Box display="inline-flex" overflow="hidden" mb={{ xs: 1.5, md: 2 }}>
                    <Box
                      display="inline-flex"
                      justifyContent="center"
                      alignItems="center"
                      width={({ spacing }) => ({ xs: spacing(2.25), md: spacing(3) })}
                    >
                      <Icon style={{ width: '100%', height: '100%' }} />
                    </Box>
                  </Box>
                )}
                <Typography
                  variant="body2"
                  fontSize={{
                    md: fontSizeValue?.toString(),
                  }}
                  lineHeight={({ typography }) => typography.body1.lineHeight}
                  fontWeight="700"
                >
                  {title}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  ) : null;
};
